import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Brand, KnpPaginationData, ListOptions, MidTuple, PatternData, SeamRequest, Store } from 'app/model';
import { MidFilter } from 'app/model/mid-filter';
import { PreProdPattern } from 'app/type';

import { BaseApiService } from './base-api.service';
import { CrisRequestType } from '../constants/cris-request-type';
import { AsyncSearchResponse } from '../model/es/async-search.response';
import { WorkflowTupleSearchForm } from '../model/form/tuple-search.form';

class SearchService extends BaseApiService {
    public search(query: string, page: number, pageSize: number): Observable<KnpPaginationData<Brand>> {
        const options = {
            params: {
                q: query,
                page: `${page}`,
                pageSize: `${pageSize}`,
            },
        };

        return this.http.get<KnpPaginationData<Brand>>(this.getAbsolutePath('search'), options);
    }

    /**
     * @deprecated use {@link search()} instead
     */
    public searchUp(query: string, page: number, pageSize: number): Observable<KnpPaginationData<Brand>> {
        return this.search(query, page, pageSize);
    }

    public searchStores(query: string, page: number, pageSize: number): Observable<KnpPaginationData<Store>> {
        return this.searchUp(query, page, pageSize).pipe(
            map((data) => ({
                ...data,
                data: data.data
                    .map((brand) => {
                        if (!brand.stores) {
                            return [];
                        }

                        return brand.stores.map((store) => {
                            const result: Store = { ...store, brand: { ...brand, stores: undefined } };

                            return result;
                        });
                    })
                    .reduce((prev, current) => current.concat(prev), []),
            })),
        );
    }

    public findTuples(
        patterns: PatternData,
        filters: MidFilter[],
        correction?: string,
        storeId?: number,
        searchAfter?: Array<number | string>,
        opts?: ListOptions,
    ): Observable<AsyncSearchResponse<MidTuple>> {
        const { extraParams, ...rest } = opts ?? {};
        const httpOpts = {
            params: {
                ...extraParams,
                ...rest,
            },
        };

        const options = {
            tuple_search_params: {
                type: CrisRequestType.STORE,
                searchParams: patterns,
                correction,
                storeId,
                searchAfter,
                filters,
            },
        };

        return this.http
            .post<AsyncSearchResponse<MidTuple>>(this.getAbsolutePath('search/mids'), options, httpOpts)
            .pipe(map((response) => new AsyncSearchResponse<MidTuple>(response)));
    }

    public findRemaining(
        request: SeamRequest,
        form: WorkflowTupleSearchForm,
        opts?: ListOptions,
    ): Observable<AsyncSearchResponse<MidTuple>> {
        const { extraParams, ...rest } = opts ?? {};
        const options = {
            params: {
                ...extraParams,
                ...rest,
            },
        };

        return this.http
            .post<AsyncSearchResponse<MidTuple>>(
                this.getAbsolutePath(`search/${request.id}/mids`),
                {
                    workflow_tuple_search: {
                        ...form,
                    },
                },
                options,
            )
            .pipe(map((response) => new AsyncSearchResponse<MidTuple>(response)));
    }

    public findPatterns<T = PreProdPattern>(
        type: CrisRequestType,
        store?: Store,
        opts?: ListOptions,
        patternId?: string,
        action?: 'MAP' | 'UNMAP',
        active?: boolean,
    ): Observable<KnpPaginationData<T>> {
        const options = {
            params: {
                pattern_search: {
                    type,
                    store: store?.id,
                    patternId,
                    action,
                    active,
                },
                ...opts,
            },
        };

        return this.http.get<KnpPaginationData<T>>(this.getAbsolutePath('search/patterns'), options);
    }

    /**
     * The id in the response array is the seam request ID responsible for that pattern.
     */
    public findActivePatterns(store: Store): Observable<
        Array<{
            type: CrisRequestType;
            outputId: number;
            id: string;
        }>
    > {
        const options = {
            params: {
                pattern_search: {
                    store: store.id,
                },
            },
        };

        return this.http.get(this.getAbsolutePath('search/patterns/active'), options);
    }

    public downloadPatterns(type: CrisRequestType, store?: Store, patternId?: string, opts?: ListOptions): void {
        const options = {
            params: {
                pattern_search: {
                    type,
                    store: store?.id,
                    ...(patternId ? { patternId } : {}),
                },
                download: 1,
                ...opts,
            },
        };

        // interceptor will take care of download handling
        this.http.get<Blob>(this.getAbsolutePath('search/patterns'), options).subscribe();
    }

    public asyncResults<T>(id: string): Observable<AsyncSearchResponse<T>> {
        return this.http
            .get<AsyncSearchResponse<T>>(this.getAbsolutePath('search/async/results'), { params: { id } })
            .pipe(map((response) => new AsyncSearchResponse<T>(response)));
    }
}

export const searchService = new SearchService();
